/**
 * @file adminAuditService.js
 * @description Admin audit log service. Appends every admin action to the
 *   admin_audit_log table. Fails silently — never crashes the calling action.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/adminAuditService
 */
// ─── Admin Audit Log Service ──────────────────────────────────────────────────
// Appends every admin action to admin_audit_log.
// Fails silently — never crashes the calling action.

import { supabase } from "./supabase";

/**
 * Log an admin action.
 * @param {object} opts
 * @param {string} opts.actionType  - e.g. 'approve_credential', 'suspend_agency', 'flag_listing'
 * @param {string} opts.targetType  - 'worker' | 'agency' | 'listing' | 'config' | 'platform'
 * @param {string} [opts.targetId]  - UUID or key of the target record
 * @param {string} [opts.targetName] - Human-readable name for display in audit log
 * @param {object} [opts.details]   - Any extra context (reason, before/after values, etc.)
 */
export async function logAdminAction({ actionType, targetType, targetId = null, targetName = "", details = {} }) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("admin_audit_log").insert({
      actor_id:    user?.id || null,
      action_type: actionType,
      target_type: targetType,
      target_id:   targetId ? String(targetId) : null,
      target_name: targetName || "",
      details,
    });
  } catch {
    // Silently fail — audit logging must never crash an admin action
  }
}

// ─── Action type constants ────────────────────────────────────────────────────
export const AUDIT = {
  APPROVE_CREDENTIAL:   "approve_credential",
  REJECT_CREDENTIAL:    "reject_credential",
  REVOKE_CREDENTIAL:    "revoke_credential",
  REREVIEW_CREDENTIAL:  "rereview_credential",
  REVIEW_CREDENTIAL:    "credential_under_review",
  REMIND_CREDENTIAL:    "credential_reminder",
  CLEAR_BGC:            "clear_bgc",
  SUSPEND_AGENCY:       "suspend_agency",
  REACTIVATE_AGENCY:    "reactivate_agency",
  FLAG_LISTING:         "flag_listing",
  UNFLAG_LISTING:       "unflag_listing",
  REMOVE_LISTING:       "remove_listing",
  ARCHIVE_STALE:        "archive_stale_listing",
  ARCHIVE_ALL_STALE:    "archive_all_stale",
  CHANGE_CONFIG:        "change_config",
  RESOLVE_ERROR:        "resolve_error",
  ACCOUNT_CANCEL_DELETION: "account_cancel_deletion",
  ACCOUNT_FORCE_PURGE:     "account_force_purge",
  ACCOUNT_QUEUE_DELETION:  "account_queue_deletion",
  ACCOUNT_BLOCK_EMAIL:            "account_block_email",
  ACCOUNT_UNBLOCK_EMAIL:          "account_unblock_email",
  ACCOUNT_REMOVE_TRIAL_REGISTRY:  "account_remove_trial_registry",
  SUSPEND_WORKER:    "suspend_worker",
  REACTIVATE_WORKER: "reactivate_worker",
  ADMIN_MANUAL_VERIFY: "admin_manual_verify",
  GRANT_FOUNDER:   "grant_founder_status",
  REVOKE_FOUNDER:  "revoke_founder_status",
};
